import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent,
  Button, Slider, TextField, CircularProgress, Alert
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { logMood, getMyMoods, getMoodSummary } from '../services/api';

const MOODS = {
  1: { emoji: '😞', label: 'Very Low',  color: '#ef4444', glow: 'rgba(239,68,68,0.35)',   bg: 'rgba(239,68,68,0.12)'   },
  2: { emoji: '😔', label: 'Low',       color: '#f97316', glow: 'rgba(249,115,22,0.35)',  bg: 'rgba(249,115,22,0.12)'  },
  3: { emoji: '😐', label: 'Okay',      color: '#eab308', glow: 'rgba(234,179,8,0.35)',   bg: 'rgba(234,179,8,0.12)'   },
  4: { emoji: '🙂', label: 'Good',      color: '#84cc16', glow: 'rgba(132,204,22,0.35)',  bg: 'rgba(132,204,22,0.12)'  },
  5: { emoji: '😊', label: 'Great',     color: '#4ade80', glow: 'rgba(74,222,128,0.35)',  bg: 'rgba(74,222,128,0.12)'  },
};

export default function Mood() {
  const navigate = useNavigate();
  const [score, setScore] = useState(3);
  const [energy, setEnergy] = useState(3);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [moods, setMoods] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => { loadMoods(); }, []);

  const loadMoods = async () => {
    try {
      const [moodsRes, summaryRes] = await Promise.all([
        getMyMoods(),
        getMoodSummary(),
      ]);
      setMoods(moodsRes.data.slice(0, 5));
      setSummary(summaryRes.data);
    } catch (e) {}
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      await logMood({ score, energy_level: energy, note });
      setSuccess(true);
      setNote('');
      loadMoods();
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Failed to log mood. Try again.');
    }
    setLoading(false);
  };

  const current = MOODS[score];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0a1628, #0d1f3c, #0a2040)', pb: 4 }}>

      {/* Header */}
      <Box sx={{
        background: 'rgba(255,255,255,0.02)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        px: 3, py: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ArrowBack sx={{ color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }} onClick={() => navigate('/dashboard')} />
          <Typography sx={{ color: 'white', fontWeight: 700 }}>Mood Check-in</Typography>
        </Box>
        <Button
          onClick={() => navigate('/mood-insights')}
          size="small"
          sx={{
            background: 'rgba(34,211,238,0.1)',
            color: '#22d3ee',
            border: '1px solid rgba(34,211,238,0.25)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.78rem',
            px: 1.5,
          }}
        >
          Insights →
        </Button>
      </Box>

      <Box sx={{ px: 3, pt: 3 }}>

        {success && <Alert severity="success" sx={{ mb: 2 }}>Mood logged! 🎉</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Mood Selector */}
        <Card sx={{
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${current.color}44`,
          borderRadius: 3,
          mb: 2,
        }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '0.12em', mb: 3 }}>
              HOW ARE YOU FEELING?
            </Typography>

            {/* Glowing emoji circle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{
                width: 110, height: 110,
                borderRadius: '50%',
                background: current.bg,
                border: `2px solid ${current.color}66`,
                boxShadow: `0 0 32px ${current.glow}, 0 0 64px ${current.glow}55`,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s ease',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: `0 0 24px ${current.glow}, 0 0 48px ${current.glow}44` },
                  '50%': { boxShadow: `0 0 40px ${current.glow}, 0 0 72px ${current.glow}66` },
                  '100%': { boxShadow: `0 0 24px ${current.glow}, 0 0 48px ${current.glow}44` },
                },
              }}>
                <Typography sx={{ fontSize: '3rem', lineHeight: 1, mb: 0.3 }}>{current.emoji}</Typography>
                <Typography sx={{ color: current.color, fontWeight: 800, fontSize: '0.82rem', letterSpacing: '0.05em' }}>
                  {current.label}
                </Typography>
              </Box>
            </Box>

            {/* Mood picker dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
              {[1, 2, 3, 4, 5].map((v) => (
                <Box
                  key={v}
                  onClick={() => setScore(v)}
                  sx={{
                    width: score === v ? 48 : 38,
                    height: score === v ? 48 : 38,
                    borderRadius: '50%',
                    background: score === v ? MOODS[v].bg : 'rgba(255,255,255,0.05)',
                    border: `2px solid ${score === v ? MOODS[v].color : 'rgba(255,255,255,0.1)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: score === v ? '1.4rem' : '1.1rem',
                    boxShadow: score === v ? `0 0 14px ${MOODS[v].glow}` : 'none',
                  }}
                >
                  {MOODS[v].emoji}
                </Box>
              ))}
            </Box>

            <Slider
              value={score}
              min={1} max={5} step={1}
              onChange={(e, val) => setScore(val)}
              sx={{
                color: current.color,
                '& .MuiSlider-thumb': {
                  width: 20, height: 20,
                  boxShadow: `0 0 10px ${current.glow}`,
                },
                '& .MuiSlider-rail': { opacity: 0.2 },
              }}
            />
          </CardContent>
        </Card>

        {/* Energy Level */}
        <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(34,211,238,0.2)', borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '0.12em', mb: 2 }}>
              ENERGY LEVEL ⚡
            </Typography>

            {/* Energy visual */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              {[1, 2, 3, 4, 5].map((v) => (
                <Box key={v} sx={{
                  width: 10,
                  height: 10 + v * 8,
                  borderRadius: 2,
                  background: v <= energy
                    ? `linear-gradient(180deg, #22d3ee, #4ade80)`
                    : 'rgba(255,255,255,0.08)',
                  transition: 'all 0.2s ease',
                  alignSelf: 'flex-end',
                }} />
              ))}
            </Box>

            <Slider
              value={energy}
              min={1} max={5} step={1}
              onChange={(e, val) => setEnergy(val)}
              sx={{
                color: '#22d3ee',
                '& .MuiSlider-thumb': { width: 20, height: 20 },
                '& .MuiSlider-rail': { opacity: 0.2 },
              }}
              marks={[1,2,3,4,5].map(v => ({ value: v, label: '' }))}
            />
          </CardContent>
        </Card>

        {/* Note */}
        <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '0.12em', mb: 1.5 }}>
              ADD A NOTE (OPTIONAL)
            </Typography>
            <TextField
              fullWidth multiline rows={3}
              placeholder="What's on your mind today?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white', fontSize: '0.9rem',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                  '&:hover fieldset': { borderColor: '#4ade80' },
                  '&.Mui-focused fieldset': { borderColor: '#4ade80' },
                },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.25)' },
              }}
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <Button
          fullWidth variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            py: 1.6,
            background: `linear-gradient(135deg, ${current.color}, #22d3ee)`,
            color: '#0a1628',
            fontWeight: 800,
            fontSize: '1rem',
            borderRadius: 2,
            boxShadow: `0 4px 20px ${current.glow}`,
            transition: 'all 0.3s ease',
            '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#0a1628' }} /> : `Log My Mood ${current.emoji}`}
        </Button>

        {/* Summary */}
        {summary && (
          <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 3, mt: 3 }}>
            <CardContent>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '0.12em', mb: 2 }}>
                YOUR MOOD STATS
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                {[
                  { label: 'Average', value: summary.average_score.toFixed(1), color: '#4ade80' },
                  { label: 'Best',    value: summary.highest_score,            color: '#22d3ee' },
                  { label: 'Total',   value: summary.total_entries,            color: '#a855f7' },
                ].map(stat => (
                  <Box key={stat.label} sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: stat.color, fontWeight: 900, fontSize: '1.6rem' }}>
                      {stat.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Recent Moods */}
        {moods.length > 0 && (
          <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 3, mt: 2 }}>
            <CardContent>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', letterSpacing: '0.12em', mb: 2 }}>
                RECENT MOODS
              </Typography>
              {moods.map(mood => {
                const m = MOODS[mood.score] || MOODS[3];
                return (
                  <Box key={mood.id} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    mb: 1, p: 1.2,
                    background: m.bg,
                    border: `1px solid ${m.color}22`,
                    borderRadius: 2,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '1.2rem' }}>{m.emoji}</Typography>
                      <Typography sx={{ color: m.color, fontWeight: 700, fontSize: '0.78rem' }}>{m.label}</Typography>
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}>
                      {mood.note ? mood.note.slice(0, 20) + (mood.note.length > 20 ? '…' : '') : 'No note'}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>
                      {new Date(mood.created_at).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}
                    </Typography>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}

      </Box>
    </Box>
  );
}